import { UseFormGetValues } from "react-hook-form";

import { UserType } from "src/types/users";
import {
  AddCaseProps,
  CaseDoctorProps,
  InsuranceAttributesProps,
  PatientAddress,
  PatientAttributes,
  PatientPhoneNumber,
  PrescriptionAttributesType,
  ProcedureProps,
  ShipmentAttributesProps,
} from "src/pages/addCase/types";
import { CaseCloneResponseType } from "src/pages/cloneCase/types";
import { format } from "date-fns";


export function populatingCloneCase(
  { data }: CaseCloneResponseType,
  userData: UserType,
  getValues: UseFormGetValues<AddCaseProps>,
  reinvestigation: boolean,
  outreach: boolean,
  pcc_outreach: boolean
): AddCaseProps {
  const caseFormData: AddCaseProps = {} as AddCaseProps;

  if (data.case) {
    caseFormData.mos = getValues("mos");
    caseFormData.user_id = userData ? userData.id : 0;
    caseFormData.creator_name = userData ? userData.name : "";
    caseFormData.submission_source = data.case.submission_source || "provider";
    caseFormData.category = data.case.category || "medrx";

    caseFormData.prescription_attributes = {} as PrescriptionAttributesType;
  }

  if (data.prescription) {
    caseFormData.prescription_attributes.qty = Number(data.prescription.qty);
    caseFormData.prescription_attributes.diagnosis_code = data.prescription.diagnosis_code.split(",")
    caseFormData.prescription_attributes.direction = data.prescription.direction || "";
    caseFormData.prescription_attributes.note = data.prescription.note || "";
    caseFormData.prescription_attributes.written_date = format(new Date(), 'MM/dd/yyyy')
  }

  if (data.prescription_attributes) {
    data.prescription_attributes.forEach((item) => {
      switch(item.name.toLowerCase()) {
        case "indication":
          caseFormData.prescription_attributes.indication = item.value || "";
          break;
      }
    })
  }

  if (data.procedure && data.procedure.length > 0) {
    caseFormData.procedure_attributes = {} as ProcedureProps;
    caseFormData.procedure_attributes.cpt_code = Number(data.procedure[0].cpt_code);
  }

  caseFormData.shipments_attributes = [{} as ShipmentAttributesProps];

  if (data.shipments && data.shipments.length > 0) {
    caseFormData.shipments_attributes[0].ship_to = 
      data.shipments[0].ship_to === "provider" ? "Provider Office" : data.shipments[0].ship_to === "patient" ? "Hospital Outpatient Dept" : data.shipments[0].ship_to?.toLowerCase() === "other" ? "Other" : data.shipments[0].ship_to || "";
    caseFormData.shipments_attributes[0].ship_to_street =
      data.shipments[0].ship_to_street || "";
    caseFormData.shipments_attributes[0].ship_to_suite =
      data.shipments[0].ship_to_suite || "";
    caseFormData.shipments_attributes[0].ship_to_city =
      data.shipments[0].ship_to_city || "";
    caseFormData.shipments_attributes[0].ship_to_state =
      data.shipments[0].ship_to_state || "";
    caseFormData.shipments_attributes[0].ship_to_zip =
      data.shipments[0].ship_to_zip || "";
  }

  if (data.doctor) {
    caseFormData.doctor_firstlastname = `${data.doctor.firstname} ${data.doctor.lastname}`;
    caseFormData.doctor_fullname = `${data.doctor.firstname} ${
      data.doctor.middlename || ""
    } ${data.doctor.lastname}`;

    caseFormData.doctor_attributes = {
      practice_id: data.doctor.practice_id,
      global_doctor_id: data.doctor.global_doctor_id,
    } as CaseDoctorProps;
  }

  if (data.practice || data.practice_address) {
    caseFormData.practice_name = `${data.practice.name} - ${data.practice_address.full_address}`;
  }

  if (data.patient) {
    const { patient } = data;

    caseFormData.patient_firstlastname = `${patient.firstname} ${patient.lastname}`;
    caseFormData.patient_fullname = `${patient.firstname} ${
      patient.middlename || ""
    } ${patient.lastname}`;
    caseFormData.patient_dob = patient.dob;

    caseFormData.patient_attributes = {
      global_patient_id: patient.global_patient_id,
      title: patient.title || "",
      firstname: patient.firstname,
      middlename: patient.middlename || "",
      lastname: patient.lastname,
      gender: patient.gender,
      dob: patient.dob,
      ssn: patient.ssn || "",
      best_time_to_contact: patient.best_time_to_contact || "",
      outreach: true,
      pcc_outreach: false,
      reinvestigation,
    } as PatientAttributes;
  }

  if (data.patient_attributes?.length > 0) {
    caseFormData.patient_attributes.reinvestigation = reinvestigation;
    caseFormData.patient_attributes.outreach = outreach;
    const new_pcc_outreach = ["Other", "PHN"].includes(caseFormData.prescription_attributes.indication) ? false : pcc_outreach
    caseFormData.patient_attributes.pcc_outreach = new_pcc_outreach;
    // data.patient_attributes.forEach((attr) => {
    //   if (attr?.name === "reinvestigation")
    //     caseFormData.patient_attributes.reinvestigation = attr?.value === "1" ? true : false
    //   else if (attr?.name === "outreach")
    //     caseFormData.patient_attributes.outreach = attr?.value === "1" ? true : false
    //   else if (attr?.name === "pcc_outreach")
    //     caseFormData.patient_attributes.pcc_outreach = attr?.value === "1" ? true : false
    // })
  }

  if (data.patient_phone_numbers && data.patient_phone_numbers.length > 0) {
    caseFormData.patient_attributes.phone_numbers = [];
    data.patient_phone_numbers.forEach((phone, index) => {
      caseFormData.patient_attributes.phone_numbers[index] =
        {} as PatientPhoneNumber;
      caseFormData.patient_attributes.phone_numbers[index].number =
        phone.number.toString();
      caseFormData.patient_attributes.phone_numbers[index].phone_type =
        phone.phone_type || "cell";
      caseFormData.patient_attributes.phone_numbers[
        index
      ].is_ok_to_leave_message = phone.is_ok_to_leave_message;
      caseFormData.patient_attributes.phone_numbers[index].is_preferred =
        phone.is_preferred;
      caseFormData.patient_attributes.phone_numbers[index].id = phone.id;
    });
  } else {
    caseFormData.patient_attributes.phone_numbers = [{
      id: null,
      is_ok_to_leave_message: false,
      is_preferred: true,
      number: "",
      phone_type: "cell",
    }]
  }

  caseFormData.patient_attributes.address = {
    address_name: "home",
  } as PatientAddress;
  if (data.patient_address) {
    caseFormData.patient_attributes.address.street =
      data.patient_address.street || "";
    caseFormData.patient_attributes.address.suite =
      data.patient_address.suite || "";
    caseFormData.patient_attributes.address.city =
      data.patient_address.city || "";
    caseFormData.patient_attributes.address.state =
      data.patient_address.state || "";
    caseFormData.patient_attributes.address.zip =
      data.patient_address.zip || "";
  }

  if (data.patient_entities_address) {
    caseFormData.patient_attributes.address.address_name =
      data.patient_entities_address.address_name || "";
  }

  caseFormData.insurances_attributes = [{} as InsuranceAttributesProps];
  if (data.insurances && data.insurances.length > 0) {
    data.insurances.forEach((insurance, index) => {
      if (caseFormData.insurances_attributes) {
        caseFormData.insurances_attributes[index] =
          {} as InsuranceAttributesProps;
        caseFormData.insurances_attributes[index].insurance_name = insurance.master_insurance_id !== null ? insurance.insurance_name : "";
        caseFormData.insurances_attributes[index].plan_name =  insurance.plan_name || ""; 
        caseFormData.insurances_attributes[index].plan_type = insurance.insurance_type || "";
        caseFormData.insurances_attributes[index].relation_to_patient =
          insurance.relation_to_patient || "self";
        caseFormData.insurances_attributes[index].relation_firstname =
          insurance.relation_firstname || "";
        caseFormData.insurances_attributes[index].relation_middlename =
          insurance.relation_middlename || "";
        caseFormData.insurances_attributes[index].relation_lastname =
          insurance.relation_lastname || "";
        caseFormData.insurances_attributes[index].policy =
          insurance.policy || "";
        caseFormData.insurances_attributes[index].group_number =
          insurance.group_number || "";
        caseFormData.insurances_attributes[index].insurance_number =
          insurance.insurance_number || "";
        caseFormData.insurances_attributes[index].member_id =
          insurance.member_id || "";
        caseFormData.insurances_attributes[index].bin = insurance.bin || "";
        caseFormData.insurances_attributes[index].pcn = insurance.pcn || "";
        caseFormData.insurances_attributes[index].rx_group =
          insurance.rx_group || "";
        caseFormData.insurances_attributes[index].global_insurance_id =
          insurance.global_insurance_id
            ? insurance.global_insurance_id.toString()
            : null;
        caseFormData.insurances_attributes[index].is_primary_insurance =
          index === 0;
        caseFormData.insurances_attributes[index].insurance_priority = index;
      }
    });
  } else {
    caseFormData.insurances_attributes = [{
      plan_name: "",
      global_insurance_id: null,
      insurance_priority: 0,
      is_primary_insurance: true,
      insurance_name: "",
      policy: "",
      relation_to_patient: "self"
    }]
  }

  return caseFormData;
}
